import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketsAPI } from '../../Api';
import {
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
  Snackbar,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as LimitIcon,
  Chat as ChatIcon
} from '@mui/icons-material';

const TicketsList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0); // 0 - Все, 1 - Мои, 2 - Свободные
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(new Date().toISOString());
  const [newTicketsCount, setNewTicketsCount] = useState(0);
  const [notification, setNotification] = useState({ open: false, message: '' });
  const [assigning, setAssigning] = useState({});
  const [flashBadge, setFlashBadge] = useState(false);
  const pollingInterval = useRef(null);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit: 50 };
      
      if (tab === 1) params.assigned_to = 'me';
      if (tab === 2) params.assigned_to = 'unassigned';
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      
      const response = await ticketsAPI.getTickets(params);
      setTickets(response.data.data.tickets);
      setTotal(response.data.data.total);
      setLastUpdate(response.data.data.timestamp);
      setNewTicketsCount(0);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(err.response?.data?.message || 'Ошибка загрузки тикетов');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTicket = async (ticketId, e) => {
    e.stopPropagation();
    
    try {
      setAssigning(prev => ({ ...prev, [ticketId]: true }));
      await ticketsAPI.assignTicket(ticketId);
      setNotification({
        open: true,
        message: '✅ Тикет взят в работу'
      });
      // Сразу открываем детали тикета
      navigate(`/tickets/${ticketId}`);
    } catch (err) {
      setNotification({
        open: true,
        message: err.response?.data?.message || 'Ошибка при взятии тикета'
      });
      setAssigning(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE');
      audio.volume = 0.5;
      audio.play().catch(e => console.log('Audio play failed:', e));
    } catch (err) {
      console.log('Notification sound error:', err);
    }
  };

  const checkNewTickets = useCallback(async () => {
    try {
      const response = await ticketsAPI.getNewTicketsCount(lastUpdate);
      const count = response.data.data.count;
      
      if (count > 0 && count !== newTicketsCount) {
        setNewTicketsCount(count);
        playNotificationSound();
        
        // Запускаем анимацию мигания badge
        setFlashBadge(true);
        setTimeout(() => setFlashBadge(false), 3000);
        
        setNotification({
          open: true,
          message: `🔔 ${count} ${count === 1 ? 'новый тикет' : 'новых тикетов'}`
        });
        
        // Показываем браузерное уведомление если разрешено
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Новые тикеты', {
            body: `${count} ${count === 1 ? 'новый тикет' : 'новых тикетов'}`,
            icon: '/favicon.ico',
            badge: '/favicon.ico'
          });
        }
      }
      
      // Обновляем список тикетов в фоне для отображения непрочитанных сообщений
      const params = { limit: 50 };
      if (tab === 1) params.assigned_to = 'me';
      if (tab === 2) params.assigned_to = 'unassigned';
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      
      const ticketsResponse = await ticketsAPI.getTickets(params);
      setTickets(ticketsResponse.data.data.tickets);
      setTotal(ticketsResponse.data.data.total);
      setLastUpdate(ticketsResponse.data.data.timestamp);
    } catch (err) {
      console.error('Error checking new tickets:', err);
    }
  }, [lastUpdate, newTicketsCount, tab, statusFilter, typeFilter]);

  useEffect(() => {
    fetchTickets();
  }, [tab, statusFilter, typeFilter]);

  useEffect(() => {
    // Запрашиваем разрешение на уведомления
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Запускаем polling каждые 10 секунд для более быстрых уведомлений
    pollingInterval.current = setInterval(() => {
      checkNewTickets();
    }, 10000);

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [checkNewTickets]);

  const getStatusStyle = (status) => {
    const styles = {
      created: { bg: '#e3f2fd', color: '#1976d2', label: 'Открытые' },
      in_progress: { bg: '#fff3e0', color: '#f57c00', label: 'В работе' },
      closed: { bg: '#f5f5f5', color: '#757575', label: 'Закрытые' },
      approved: { bg: '#e8f5e9', color: '#388e3c', label: 'Одобрен' },
      rejected: { bg: '#ffebee', color: '#d32f2f', label: 'Отклонен' }
    };
    return styles[status] || styles.created;
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'только что';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} мин`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} ч`;
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  };

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1.5 }, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          Тикеты
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {newTicketsCount > 0 && (
            <Chip 
              label={`+${newTicketsCount}`} 
              size="small" 
              color="error" 
              sx={{ 
                height: 22, 
                fontWeight: 600,
                animation: flashBadge ? 'pulse 1s ease-in-out 3' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.2)', opacity: 0.8 }
                }
              }}
            />
          )}
          <IconButton size="small" onClick={fetchTickets} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 1.5 }}>
        {/* Status Filter */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.75, flexWrap: 'wrap' }}>
          {['', 'created', 'in_progress', 'closed', 'approved'].map(s => (
            <Chip
              key={s || 'all'}
              label={s ? getStatusStyle(s).label : 'Все'}
              size="small"
              onClick={() => setStatusFilter(s)}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                bgcolor: statusFilter === s ? (s ? getStatusStyle(s).bg : '#e0e0e0') : 'transparent',
                color: statusFilter === s ? (s ? getStatusStyle(s).color : '#333') : '#666',
                border: '1px solid',
                borderColor: statusFilter === s ? (s ? getStatusStyle(s).color : '#999') : '#ddd',
                fontWeight: statusFilter === s ? 600 : 400,
                '&:hover': { bgcolor: s ? getStatusStyle(s).bg : '#f5f5f5' }
              }}
            />
          ))}
        </Box>

        {/* Type Filter */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {[
            { value: '', label: 'Все типы', icon: '📋' },
            { value: 'payment_issue', label: 'Оплата', icon: '💳' },
            { value: 'limit_increase', label: 'Лимиты', icon: '📈' }
          ].map(t => (
            <Chip
              key={t.value || 'all-types'}
              label={`${t.icon} ${t.label}`}
              size="small"
              onClick={() => setTypeFilter(t.value)}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                bgcolor: typeFilter === t.value ? '#e3f2fd' : 'transparent',
                color: typeFilter === t.value ? '#1976d2' : '#666',
                border: '1px solid',
                borderColor: typeFilter === t.value ? '#1976d2' : '#ddd',
                fontWeight: typeFilter === t.value ? 600 : 400,
                '&:hover': { bgcolor: '#e3f2fd' }
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Loading */}
      {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      {error && <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{error}</Alert>}

      {/* Tickets List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {tickets.map((ticket) => {
          const status = getStatusStyle(ticket.status);
          const canOpen = ticket.assigned_to || ticket.status === 'closed';
          
          return (
            <Box
              key={ticket._id}
              onClick={() => canOpen && navigate(`/tickets/${ticket._id}`)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                bgcolor: ticket.unread_client_messages > 0 ? '#fff8e1' : '#fff',
                borderRadius: 1.5,
                border: ticket.unread_client_messages > 0 ? '1px solid #ffc107' : '1px solid #eee',
                cursor: canOpen ? 'pointer' : 'default',
                transition: 'all 0.15s',
                '&:hover': canOpen ? { 
                  bgcolor: ticket.unread_client_messages > 0 ? '#fff3cd' : '#fafafa', 
                  borderColor: ticket.unread_client_messages > 0 ? '#ffa000' : '#ddd',
                  transform: 'translateX(2px)'
                } : {}
              }}
            >
              {/* Icon */}
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: ticket.type === 'limit_increase' ? '#e8f5e9' : '#e3f2fd',
                  color: ticket.type === 'limit_increase' ? '#388e3c' : '#1976d2'
                }}
              >
                {ticket.type === 'limit_increase' ? <LimitIcon fontSize="small" /> : <MoneyIcon fontSize="small" />}
              </Avatar>

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    #{ticket._id.slice(-6)}
                  </Typography>
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.1,
                      borderRadius: 0.5,
                      bgcolor: status.bg,
                      color: status.color,
                      fontSize: '0.65rem',
                      fontWeight: 600
                    }}
                  >
                    {status.label}
                  </Box>
                  {ticket.unread_client_messages > 0 && (
                    <Chip
                      label={ticket.unread_client_messages}
                      size="small"
                      color="error"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        minWidth: 18,
                        '& .MuiChip-label': { px: 0.5 },
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.1)' }
                        }
                      }}
                    />
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666', fontSize: '0.75rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <PersonIcon sx={{ fontSize: 12 }} />
                    <span>{ticket.tg_id?.userId || '—'}</span>
                  </Box>
                  {ticket.order && (
                    <span>• {Number(ticket.order.total_amount || 0)}₽</span>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <TimeIcon sx={{ fontSize: 12 }} />
                    <span>{formatTime(ticket.created_at)}</span>
                  </Box>
                </Box>

                {ticket.client?.[0]?.message && (
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: '#888',
                      mt: 0.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    💬 {ticket.client[0].message}
                  </Typography>
                )}
              </Box>

              {/* Action */}
              {!ticket.assigned_to && ticket.status !== 'closed' ? (
                <Tooltip title="Взять в работу">
                  <IconButton
                    size="small"
                    onClick={(e) => handleAssignTicket(ticket._id, e)}
                    disabled={assigning[ticket._id]}
                    sx={{
                      bgcolor: '#4caf50',
                      color: '#fff',
                      width: 32,
                      height: 32,
                      '&:hover': { bgcolor: '#388e3c' }
                    }}
                  >
                    {assigning[ticket._id] ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PlayArrowIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              ) : ticket.assigned_to ? (
                <Tooltip title={ticket.assigned_to.username}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: '#667eea' }}>
                    {ticket.assigned_to.username?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                </Tooltip>
              ) : null}
            </Box>
          );
        })}
      </Box>

      {/* Empty state */}
      {!loading && tickets.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4, color: '#999' }}>
          <ChatIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography>Тикеты не найдены</Typography>
        </Box>
      )}

      {/* Footer */}
      {tickets.length > 0 && (
        <Typography sx={{ textAlign: 'center', mt: 1.5, fontSize: '0.7rem', color: '#999' }}>
          Показано {tickets.length} из {total}
        </Typography>
      )}

      {/* Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ open: false, message: '' })}
        message={notification.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default TicketsList;
